/**
 * In-form Google Drive folder browser using user OAuth + Drive API list/search.
 * Works with client ID only; Google Picker API key is optional (see Browse in picker button).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, Folder, FolderOpen, Loader2, RefreshCw, Search } from 'lucide-react';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import {
  getGoogleClientId,
  getGooglePickerConfig,
  listGoogleDriveItems,
  requestGoogleDriveAccessToken,
  type GoogleDriveListItem,
} from '../../utils/googlePicker';
import { GoogleDrivePickerButton } from './GoogleDrivePickerButton';

export interface GoogleDriveBrowserSelection {
  id: string;
  name: string;
}

export interface GoogleDriveBrowserProps {
  onSelect: (selection: GoogleDriveBrowserSelection) => void;
  selectedId?: string;
  selectedLabel?: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

export function GoogleDriveBrowser({
  onSelect,
  selectedId,
  selectedLabel,
}: GoogleDriveBrowserProps) {
  const clientConfigured = getGoogleClientId() != null;
  const pickerConfigured = getGooglePickerConfig() != null;

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<GoogleDriveListItem[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: 'root', name: 'My Drive' }]);

  const currentFolderId = breadcrumbs[breadcrumbs.length - 1]?.id ?? 'root';

  const loadItems = useCallback(
    async (token: string, folderId: string, query: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listGoogleDriveItems({
          accessToken: token,
          parentId: folderId,
          searchQuery: query || undefined,
        });
        setItems(result.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load Drive folders');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!accessToken) return;
    loadItems(accessToken, currentFolderId, searchQuery);
  }, [accessToken, currentFolderId, searchQuery, loadItems]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const token = await requestGoogleDriveAccessToken('consent');
      setAccessToken(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = () => {
    setAccessToken(null);
    setItems([]);
    setBreadcrumbs([{ id: 'root', name: 'My Drive' }]);
    setSearchInput('');
    setSearchQuery('');
    setError(null);
  };

  const navigateTo = (index: number) => {
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
    setSearchInput('');
    setSearchQuery('');
  };

  const openFolder = (item: GoogleDriveListItem) => {
    if (!item.isFolder) return;
    setBreadcrumbs((prev) => [...prev, { id: item.id, name: item.name }]);
    setSearchInput('');
    setSearchQuery('');
  };

  const selectFolder = (item: GoogleDriveListItem) => {
    if (!item.isFolder) return;
    onSelect({ id: item.id, name: item.name });
  };

  if (!clientConfigured) {
    return (
      <p className="text-sm text-amber-700 dark:text-amber-300">
        Google Drive browse requires VITE_GOOGLE_CLIENT_ID. Use Advanced options below to paste folder IDs manually.
      </p>
    );
  }

  if (!accessToken) {
    return (
      <div className="space-y-2">
        <Button type="button" variant="outline" onClick={handleConnect} disabled={connecting} className="flex items-center gap-2">
          {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderOpen className="h-4 w-4" />}
          {connecting ? 'Connecting...' : 'Connect Google Drive'}
        </Button>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Sign in with Google to browse and select a folder. TraceOS sync still uses a service account (Advanced).
        </p>
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 dark:border-slate-600 p-3 bg-gray-50/50 dark:bg-slate-900/30">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Google Drive connected</p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => accessToken && loadItems(accessToken, currentFolderId, searchQuery)}
            disabled={loading}
            className="flex items-center gap-1"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleDisconnect}>
            Disconnect
          </Button>
        </div>
      </div>

      {pickerConfigured && (
        <GoogleDrivePickerButton
          onSelect={(selection) => onSelect({ id: selection.id, name: selection.name })}
          className="pt-1"
        />
      )}

      <div className="flex flex-wrap items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.id}>
            {index > 0 && <ChevronRight className="h-3 w-3 shrink-0" />}
            <button
              type="button"
              onClick={() => navigateTo(index)}
              className="hover:text-primary-600 dark:hover:text-primary-400 hover:underline truncate max-w-[140px]"
              title={crumb.name}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setSearchQuery(searchInput.trim());
        }}
      >
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search folders in this location..."
          className="flex-1"
        />
        <Button type="submit" variant="outline" size="sm" className="flex items-center gap-1 shrink-0">
          <Search className="h-4 w-4" />
          Search
        </Button>
      </form>

      {selectedLabel && selectedId && (
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Selected folder: <span className="font-medium">{selectedLabel}</span>
        </p>
      )}

      <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No folders found</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-slate-700">
            {items
              .filter((item) => item.isFolder)
              .map((item) => (
                <li key={item.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/50">
                  <Folder className="h-4 w-4 text-amber-500 shrink-0" />
                  <button
                    type="button"
                    onClick={() => openFolder(item)}
                    className="flex-1 text-left text-sm truncate hover:text-primary-600 dark:hover:text-primary-400"
                    title={`Open ${item.name}`}
                  >
                    {item.name}
                  </button>
                  <Button
                    type="button"
                    size="sm"
                    variant={selectedId === item.id ? 'primary' : 'outline'}
                    onClick={() => selectFolder(item)}
                  >
                    {selectedId === item.id ? 'Selected' : 'Select'}
                  </Button>
                </li>
              ))}
          </ul>
        )}
      </div>

      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Share the selected folder with your TraceOS service account email before syncing.
      </p>
    </div>
  );
}
